import {
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    signal,
    SimpleChanges
} from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { AttachmentEntityType, AttachmentResponse } from '../../models/Attachment-model';
import { AttachmentService } from '../../service/attachment.service';
import { AuthService } from '../../service/auth.service';

export interface UploadItem {
    file: File;
    progress: number;
    previewUrl: string | null;
    subscription: Subscription;
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);

const EXTENSION_COLORS: Record<string, string> = {
    zip: '#E8A838',
    rar: '#E8A838',
    '7z': '#E8A838',
    json: '#56B6C2',
    xml: '#56B6C2',
    csv: '#56B6C2',
    pdf: '#E05252',
    doc: '#4A90D9',
    docx: '#4A90D9',
    xls: '#51A351',
    xlsx: '#51A351',
    txt: '#8A8DC1',
    ini: '#8A8DC1',
    bin: '#6C6F93',
    hex: '#6C6F93',
    default: '#7770CD'
};

@Component({
    selector: 'app-file-attachments-container',
    templateUrl: './file-attachments-container.component.html',
    styleUrls: ['./file-attachments-container.component.css']
})
export class FileAttachmentsContainerComponent implements OnChanges, OnDestroy {
    @Input() entityType!: AttachmentEntityType;
    @Input() entityId!: number;

    private attachmentService = inject(AttachmentService);
    authService = inject(AuthService) as AuthService;

    attachments = signal<AttachmentResponse[]>([]);
    uploadQueue = signal<UploadItem[]>([]);
    pendingDeletes = signal<Set<number>>(new Set());
    isLoading = signal<boolean>(false);
    isDragOver = signal<boolean>(false);
    previewUrls = signal<Map<number, string>>(new Map());

    private deleteTimers = new Map<number, ReturnType<typeof setTimeout>>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entityId'] || changes['entityType']) {
            if (this.entityId != null && this.entityId > 0 && this.entityType) {
                this.loadAttachments();
            }
        }
    }

    ngOnDestroy(): void {
        this.deleteTimers.forEach((timer) => clearTimeout(timer));
        this.uploadQueue().forEach((item) => item.subscription.unsubscribe());
        this.previewUrls().forEach((url) => URL.revokeObjectURL(url));
    }

    loadAttachments(): void {
        this.isLoading.set(true);
        this.attachmentService.listAttachments(this.entityType, this.entityId).subscribe({
            next: (data) => {
                this.attachments.set(data);
                this.isLoading.set(false);
                this.loadImagePreviews(data);
            },
            error: () => {
                this.isLoading.set(false);
            }
        });
    }

    private loadImagePreviews(attachments: AttachmentResponse[]): void {
        const images = attachments.filter((a) => this.isImage(a));
        images.forEach((attachment) => {
            this.attachmentService
                .downloadAttachmentBlob(this.entityType, attachment.id)
                .subscribe({
                    next: (blob) => {
                        const url = URL.createObjectURL(blob);
                        this.previewUrls.update((m) => {
                            const updated = new Map(m);
                            updated.set(attachment.id, url);
                            return updated;
                        });
                    }
                });
        });
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(true);
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(false);
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(false);
        const files = event.dataTransfer?.files;
        if (files) {
            for (let i = 0; i < files.length; i++) {
                this.startUpload(files[i]);
            }
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            for (let i = 0; i < input.files.length; i++) {
                this.startUpload(input.files[i]);
            }
            input.value = '';
        }
    }

    private startUpload(file: File): void {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        const previewUrl = IMAGE_EXTENSIONS.has(ext) ? URL.createObjectURL(file) : null;

        const uploadItem: UploadItem = {
            file,
            progress: 0,
            previewUrl,
            subscription: null!
        };

        uploadItem.subscription = this.attachmentService
            .uploadAttachment(this.entityType, this.entityId, file)
            .subscribe({
                next: (event) => {
                    if (event.type === HttpEventType.UploadProgress && event.total) {
                        uploadItem.progress = Math.round((100 * event.loaded) / event.total);
                        this.uploadQueue.update((q) => [...q]);
                    }
                    if (event.type === HttpEventType.Response && event.body) {
                        if (previewUrl) {
                            URL.revokeObjectURL(previewUrl);
                        }
                        this.uploadQueue.update((q) => q.filter((u) => u !== uploadItem));
                        const newAttachment = event.body!;
                        this.attachments.update((list) => [newAttachment, ...list]);
                        if (this.isImage(newAttachment)) {
                            this.loadImagePreviews([newAttachment]);
                        }
                    }
                },
                error: () => {
                    if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                    }
                    this.uploadQueue.update((q) => q.filter((u) => u !== uploadItem));
                }
            });

        this.uploadQueue.update((q) => [...q, uploadItem]);
    }

    onDelete(attachment: AttachmentResponse): void {
        const pending = new Set(this.pendingDeletes());
        pending.add(attachment.id);
        this.pendingDeletes.set(pending);

        const timer = setTimeout(() => {
            this.attachmentService.deleteAttachment(this.entityType, attachment.id).subscribe({
                next: () => {
                    this.attachments.update((list) => list.filter((a) => a.id !== attachment.id));
                    const updated = new Set(this.pendingDeletes());
                    updated.delete(attachment.id);
                    this.pendingDeletes.set(updated);
                },
                error: () => {
                    const updated = new Set(this.pendingDeletes());
                    updated.delete(attachment.id);
                    this.pendingDeletes.set(updated);
                }
            });
            this.deleteTimers.delete(attachment.id);
        }, 5000);

        this.deleteTimers.set(attachment.id, timer);
    }

    onUndoDelete(attachmentId: number): void {
        const timer = this.deleteTimers.get(attachmentId);
        if (timer) {
            clearTimeout(timer);
            this.deleteTimers.delete(attachmentId);
        }
        const updated = new Set(this.pendingDeletes());
        updated.delete(attachmentId);
        this.pendingDeletes.set(updated);
    }

    onDownload(attachment: AttachmentResponse): void {
        this.attachmentService.downloadAttachment(
            this.entityType,
            attachment.id,
            attachment.fileName
        );
    }

    isImage(attachment: AttachmentResponse): boolean {
        return IMAGE_EXTENSIONS.has((attachment.fileExtension ?? '').toLowerCase());
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    getExtensionColor(extension: string): string {
        const ext = (extension ?? '').toLowerCase();
        return EXTENSION_COLORS[ext] ?? EXTENSION_COLORS['default'];
    }

    canModify(): boolean {
        return this.authService.isSuperAdmin()
            || this.authService.isAdmin()
            || this.authService.isModerator();
    }
}
